import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, Button, MenuItem, Paper, TextField, Typography } from '@mui/material';
import React from 'react';

import { ProgramWeeks } from '../../api/programs.interface';
import { createProgramWeekOptions, getProgramWeekFilledDaysLabel, getProgramWeekStatistics } from './programs.utils';

interface Props {
    onAddWeek?: () => void;
    onSelectWeek: (week: number) => void;
    selectedWeek: number;
    variant?: 'detail' | 'rail';
    weeks: ProgramWeeks;
}

const ProgramWeekNavigator: React.FC<Props> = ({ onAddWeek, onSelectWeek, selectedWeek, variant = 'detail', weeks }) => {
    const weekOptions = createProgramWeekOptions(weeks);
    const selectedWeekIndex = weekOptions.findIndex((option) => option.value === selectedWeek);
    const previousWeek = selectedWeekIndex > 0 ? weekOptions[selectedWeekIndex - 1]?.value : undefined;
    const nextWeek = selectedWeekIndex >= 0 && selectedWeekIndex < weekOptions.length - 1 ? weekOptions[selectedWeekIndex + 1]?.value : undefined;

    if (variant === 'rail') {
        return (
            <>
                <Box sx={{ display: { sm: 'none', xs: 'grid' }, gap: 1.5 }}>
                    <TextField
                        fullWidth
                        label="Selected Week"
                        onChange={(event) => onSelectWeek(Number(event.target.value))}
                        select
                        size="small"
                        value={String(selectedWeek)}
                    >
                        {weekOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{`${option.label} • ${getProgramWeekFilledDaysLabel(weeks, option.value)}`}</MenuItem>
                        ))}
                    </TextField>
                    {onAddWeek && (
                        <Button onClick={onAddWeek} size="small" startIcon={<AddRoundedIcon />} variant="outlined">
                            Add Week
                        </Button>
                    )}
                </Box>
                <Box sx={{ display: { sm: 'grid', xs: 'none' }, gap: 1.25 }}>
                    {onAddWeek && (
                        <Button onClick={onAddWeek} size="small" startIcon={<AddRoundedIcon />} variant="outlined">
                            Add Week
                        </Button>
                    )}
                    <Box sx={{ display: 'grid', gap: 1, maxHeight: 560, overflowY: 'auto', pr: 0.5 }}>
                        {weekOptions.map((option) => {
                            const statistics = getProgramWeekStatistics(weeks, option.value);
                            const selected = option.value === selectedWeek;

                            return (
                                <Paper
                                    key={option.value}
                                    onClick={() => onSelectWeek(option.value)}
                                    sx={{
                                        borderColor: selected ? 'primary.main' : 'divider',
                                        bgcolor: selected ? 'rgba(25,118,210,0.06)' : 'background.paper',
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        p: 1.25,
                                    }}
                                    variant="outlined"
                                >
                                    <Typography sx={{ fontWeight: 700 }} variant="body2">{option.label}</Typography>
                                    <Typography color="text.secondary" variant="caption">{statistics.filledDays}/{statistics.totalDays} complete</Typography>
                                </Paper>
                            );
                        })}
                    </Box>
                </Box>
            </>
        );
    }

    return (
        <Paper sx={{ borderRadius: 3, p: 1.5 }} variant="outlined">
            <Box sx={{ alignItems: { md: 'center', xs: 'stretch' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 1.5, justifyContent: 'space-between' }}>
                <Button disabled={!previousWeek} onClick={() => previousWeek && onSelectWeek(previousWeek)} startIcon={<ChevronLeftRoundedIcon />} variant="outlined">
                    Previous
                </Button>
                <Box sx={{ alignItems: 'center', display: 'flex', flex: 1, gap: 1, justifyContent: 'center' }}>
                    <Typography sx={{ display: { md: 'block', xs: 'none' }, fontWeight: 700 }} variant="body2">
                        {`Week ${selectedWeek} of ${weekOptions.length}`}
                    </Typography>
                    <TextField
                        onChange={(event) => onSelectWeek(Number(event.target.value))}
                        select
                        size="small"
                        sx={{ minWidth: { md: 260, xs: '100%' } }}
                        value={String(selectedWeek)}
                    >
                        {weekOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{`${option.label} • ${getProgramWeekFilledDaysLabel(weeks, option.value)}`}</MenuItem>
                        ))}
                    </TextField>
                </Box>
                <Button disabled={!nextWeek} endIcon={<ChevronRightRoundedIcon />} onClick={() => nextWeek && onSelectWeek(nextWeek)} variant="outlined">
                    Next
                </Button>
            </Box>
        </Paper>
    );
};

export default ProgramWeekNavigator;